import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { createButtonPressAnimation, attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Graphics, Container } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import {
  setAutoplaySpeed,
  setPendingAutoplaySpeed,
  closeMenu,
  closeSymbolPopup,
} from "../../../../State/actions";
import {
  selectAutoPlaySpeed,
  selectPendingAutoPlaySpeed,
  selectIsSpinning,
  selectHoldAndWinActive,
  selectFeatureSpinActive,
  selectWindupComplete,
  selectShowHoldAndWinCollector,
} from "../../../../State/selectors";
import { useReelControl } from "../../../../Contexts/ReelControlContext";
import { useHoldAndWinControl } from "../../../../Contexts/HoldAndWinControlContext";
import { useHoldAndWinCollectionController } from "../../../../Hooks/useHoldAndWinCollectionController";
import store from "../../../../State/Store/store";

// Rate limiting for button clicks
let lastClickTime = 0;
const MIN_CLICK_GAP = 100; // Minimum safe gap to prevent state corruption while maintaining responsiveness

// EVENT-LEVEL THROTTLE: Blocks click handler execution at entry point
// Prevents performance degradation from rapid clicking (50+ clicks/sec)
let clickThrottleActive = false;
let clickThrottleTimeout = null;

// Turbo button configuration - matches UI button dimensions
const TURBO_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.965,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.75,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.3,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.7,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const ButtonTurbo = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();
  const { handleManualStop } = useReelControl();
  const { handleManualStop: handleHoldAndWinManualStop } = useHoldAndWinControl();
  const { triggerCollectionSkip } = useHoldAndWinCollectionController();

  // Get current speed and game state
  const autoPlaySpeed = useSelector(selectAutoPlaySpeed);
  const pendingAutoPlaySpeed = useSelector(selectPendingAutoPlaySpeed);
  const isSpinning = useSelector(selectIsSpinning);
  const windupComplete = useSelector(selectWindupComplete);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const featureSpinActive = useSelector(selectFeatureSpinActive);
  const showHoldAndWinCollector = useSelector(selectShowHoldAndWinCollector);

  // Determine current effective speed (pending takes precedence if set)
  const currentSpeed = pendingAutoPlaySpeed || autoPlaySpeed;

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click (locked for duration of spin)
  const isProcessingClickRef = useRef(false);

  // Track if manual stop has been called for the current spin
  const manualStopCalledThisSpinRef = useRef(false);

  // Track pending speed change to apply after spin completes
  const pendingSpeedChangeRef = useRef(null);

  // Track visual state for button appearance (separate from actual speed state)
  const visuallyActiveTurboRef = useRef(false);

  // CRITICAL: Action gating to prevent rapid clicks and respect windup phase
  // Controls which action type is currently allowed: 'TURBO_TOGGLE', or null (no action)
  const allowedActionRef = useRef(null);

  // CRITICAL: Pending stop mechanism - allows user to queue a turbo toggle during windup
  // User can click turbo immediately after spin starts, but stop waits for windup to complete
  const pendingStopRef = useRef(false);

  // Determine if turbo is active (current or pending)
  // Use visual ref for immediate feedback, fallback to actual state
  const isTurboActive = visuallyActiveTurboRef.current || currentSpeed === "turbo" || pendingAutoPlaySpeed === "turbo";

  const texture = PIXI.Assets.get(
    isTurboActive
      ? ASSET_NAME.icon_turbo_active
      : ASSET_NAME.icon_turbo
  );

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(TURBO_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * 1.65,
          y: -dimensions.height / 5.25,
        };
      } else {
        // landscape
        return {
          x: toolbar.width - buttonConfig.width * 0.775,
          y: -dimensions.height / 2 + buttonConfig.height * 3.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width / 2 + buttonConfig.width * 1.65,
          y: -dimensions.height / 5.1,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.9,
          y: -buttonConfig.height * 1.65,
        };
      }
    } else {
      return {
        x: toolbar.width - buttonConfig.width,
        y: toolbar.height / 2,
      };
    }
  }, [device, orientation, dimensions.height, toolbar.width, toolbar.height, buttonConfig.width, buttonConfig.height]);

  const handleClick = () => {
    // EVENT-LEVEL THROTTLE: FIRST LINE - Instant return if throttle is active
    // Prevents performance degradation from rapid clicking (50+ clicks/sec)
    if (clickThrottleActive) {
      return; // Instant exit - zero overhead, no logs, no code execution
    }

    // Set throttle flag IMMEDIATELY and schedule single reset
    clickThrottleActive = true;
    if (clickThrottleTimeout) {
      clearTimeout(clickThrottleTimeout);
    }
    clickThrottleTimeout = setTimeout(() => {
      clickThrottleActive = false;
      clickThrottleTimeout = null;
    }, MIN_CLICK_GAP);

    // ALWAYS provide visual feedback first (before any checks)
    soundManager.play(SOUND_NAME.button_click);
    if (buttonSpriteRef.current && !isPressAnimatingRef.current) {
      isPressAnimatingRef.current = true;
      createButtonPressAnimation(buttonSpriteRef.current, {
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height,
        onComplete: () => {
          isPressAnimatingRef.current = false;
        }
      });
    }

    // Rate limiting - prevent rapid spam clicks (secondary check)
    const now = Date.now();
    if (now - lastClickTime < MIN_CLICK_GAP) {
      return; // Visual feedback played, but action blocked
    }
    lastClickTime = now;

    // Close menu if open
    dispatch(closeMenu());
    dispatch(closeSymbolPopup());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // If collector is showing, enable turbo mode and skip collection animation
    if (showHoldAndWinCollector) {
      // Enable turbo mode if not already active
      if (currentSpeed !== "turbo") {
        dispatch(setAutoplaySpeed("turbo"));
        visuallyActiveTurboRef.current = true;

        // Update texture to show active state (yellow)
        if (buttonSpriteRef.current) {
          const newTexture = PIXI.Assets.get(ASSET_NAME.icon_turbo_active);
          buttonSpriteRef.current.texture = newTexture;
        }
      }

      // Trigger faster collection
      triggerCollectionSkip();
      return;
    }

    // Action gating - handle different phases with pending stop support
    if (allowedActionRef.current === 'TURBO_TOGGLE') {
      // Allowed, proceed normally
    } else if (allowedActionRef.current === null && (isSpinning || featureSpinActive) && !windupComplete) {
      // During windup, queue the turbo toggle (only once)
      if (!pendingStopRef.current) {
        pendingStopRef.current = true;
      }
      return; // Don't execute yet, will execute in windup complete effect
    } else {
      // Not allowed at all
      return;
    }

    // If spinning, only allow ONE action per spin - lock button completely after first click
    if ((isSpinning || featureSpinActive) && isProcessingClickRef.current) {
      return;
    }

    // Toggle between "auto" and "turbo" speed
    const newSpeed = currentSpeed === "turbo" ? "auto" : "turbo";

    // If spinning, trigger manual stop ONCE per spin (like ButtonPlay)
    if (isSpinning || featureSpinActive) {
      // Lock action gate to prevent any more actions until spin completes
      allowedActionRef.current = null;

      // Lock button for the entire duration of this spin (prevents multiple actions per spin)
      isProcessingClickRef.current = true;

      // Trigger manual stop once per spin when switching to turbo
      if (newSpeed === "turbo" && !manualStopCalledThisSpinRef.current) {
        if (holdAndWinActive && featureSpinActive) {
          handleHoldAndWinManualStop();
          manualStopCalledThisSpinRef.current = true;
        } else if (isSpinning) {
          handleManualStop();
          manualStopCalledThisSpinRef.current = true;
        }
      }

      // Store pending speed change in ref (will be applied after spin completes)
      // DO NOT dispatch during active spin - it causes re-renders that corrupt reel state
      pendingSpeedChangeRef.current = newSpeed;

      // Update visual state immediately for instant feedback
      visuallyActiveTurboRef.current = (newSpeed === "turbo");

      // Force texture update by directly changing the sprite texture
      if (buttonSpriteRef.current) {
        const newTexture = PIXI.Assets.get(
          newSpeed === "turbo" ? ASSET_NAME.icon_turbo_active : ASSET_NAME.icon_turbo
        );
        buttonSpriteRef.current.texture = newTexture;
      }
    } else {
      // Not spinning - check if autoplay is active
      const state = store.getState();

      if (state.settings.autoPlayActive) {
        // During autoplay, ALWAYS use pending speed (even when not spinning)
        // This prevents race condition with autoplay effect triggering next spin
        dispatch(setPendingAutoplaySpeed(newSpeed));
        pendingSpeedChangeRef.current = newSpeed;
      } else {
        // Manual mode - apply immediately
        dispatch(setAutoplaySpeed(newSpeed));
      }

      // Update visual state
      visuallyActiveTurboRef.current = (newSpeed === "turbo");
    }
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Always allow hover effect for better UX
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [
    buttonSpriteRef,
    brightnessFilter,
  ]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Initialize action gate on mount
  useEffect(() => {
    // On mount, if not spinning, allow turbo toggle
    if (!isSpinning) {
      allowedActionRef.current = 'TURBO_TOGGLE';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount - isSpinning checked but not in deps intentionally

  // CRITICAL: Action gate management - controls which action is allowed at each phase
  // This prevents rapid clicks by blocking actions that don't match the current game phase
  useEffect(() => {
    // When windup completes and we're spinning, allow TURBO_TOGGLE action (can trigger stop)
    if (isSpinning && windupComplete) {
      allowedActionRef.current = 'TURBO_TOGGLE';

      // Check if there's a pending turbo toggle and execute it
      if (pendingStopRef.current) {
        pendingStopRef.current = false;
        allowedActionRef.current = null; // Lock immediately after executing

        // Execute the queued turbo toggle with manual stop
        const newSpeed = currentSpeed === "turbo" ? "auto" : "turbo";
        isProcessingClickRef.current = true;

        if (newSpeed === "turbo" && !manualStopCalledThisSpinRef.current) {
          if (holdAndWinActive && featureSpinActive) {
            handleHoldAndWinManualStop();
            manualStopCalledThisSpinRef.current = true;
          } else if (isSpinning) {
            handleManualStop();
            manualStopCalledThisSpinRef.current = true;
          }
        }

        // Store pending speed change
        pendingSpeedChangeRef.current = newSpeed;
        visuallyActiveTurboRef.current = (newSpeed === "turbo");

        // Update texture
        if (buttonSpriteRef.current) {
          const newTexture = PIXI.Assets.get(
            newSpeed === "turbo" ? ASSET_NAME.icon_turbo_active : ASSET_NAME.icon_turbo
          );
          buttonSpriteRef.current.texture = newTexture;
        }
      }
    }
  }, [isSpinning, windupComplete, currentSpeed, holdAndWinActive, featureSpinActive, handleManualStop, handleHoldAndWinManualStop]);

  // When spin completes, allow TURBO_TOGGLE action for next spin
  useEffect(() => {
    if (!isSpinning && !featureSpinActive) {
      allowedActionRef.current = 'TURBO_TOGGLE';
      pendingStopRef.current = false; // Clear any pending turbo toggle
    }
  }, [isSpinning, featureSpinActive]);

  // Sync visual state with actual Redux state
  useEffect(() => {
    const shouldBeActive = currentSpeed === "turbo" || pendingAutoPlaySpeed === "turbo";
    visuallyActiveTurboRef.current = shouldBeActive;

    // Update texture to match actual state
    if (buttonSpriteRef.current) {
      const newTexture = PIXI.Assets.get(
        shouldBeActive ? ASSET_NAME.icon_turbo_active : ASSET_NAME.icon_turbo
      );
      buttonSpriteRef.current.texture = newTexture;
    }
  }, [currentSpeed, pendingAutoPlaySpeed]);


  // Reset flags when spin ends - unlock button for next spin
  useEffect(() => {
    if (!isSpinning && !featureSpinActive) {
      // Apply pending speed change if one was queued during the spin
      if (pendingSpeedChangeRef.current !== null) {
        dispatch(setPendingAutoplaySpeed(pendingSpeedChangeRef.current));
        pendingSpeedChangeRef.current = null;
      }

      // Reset all flags and action gate for next spin
      manualStopCalledThisSpinRef.current = false;
      isProcessingClickRef.current = false;
      pendingStopRef.current = false; // Clear any pending turbo toggle
      allowedActionRef.current = 'TURBO_TOGGLE';
    }
  }, [isSpinning, featureSpinActive, dispatch]);

  // Clean up hover effects on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }

      // Cleanup throttle timeout
      if (clickThrottleTimeout) {
        clearTimeout(clickThrottleTimeout);
        clickThrottleTimeout = null;
        clickThrottleActive = false;
      }
    };
  }, []);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(
    () => buttonConfig.width / 2,
    [buttonConfig.width]
  );

  return (
    <Container
      name="secondary_button_turbo"
      x={buttonPosition.x}
      y={buttonPosition.y}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_turbo_interactive_area"
        eventMode="static"
        cursor="pointer"
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="button_turbo_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        eventMode="none"
        alpha={1}
        resolution={2}
      />
    </Container>
  );
};

export default ButtonTurbo;
